import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { theme } from '../theme';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel: string;
}

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: theme.overlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  modal: {
    width: '100%',
    maxWidth: '440px',
    background: theme.bgPanel,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.border}`,
    padding: '32px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '12px',
  },
  message: {
    fontSize: '14px',
    color: theme.textSecondary,
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  btn: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    fontSize: '14px',
    background: 'transparent',
    color: theme.textSecondary,
  },
  btnDanger: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    background: theme.danger,
    color: theme.accentText,
  },
};

function ConfirmModal({
  options,
  onAnswer,
}: {
  options: ConfirmOptions;
  onAnswer: (ok: boolean) => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAnswer(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onAnswer]);

  return (
    <div style={styles.overlay} onClick={() => onAnswer(false)}>
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.title}>{options.title}</div>
        <div style={styles.message}>{options.message}</div>
        <div style={styles.actions}>
          <button type="button" style={styles.btn} onClick={() => onAnswer(false)}>
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            style={styles.btnDanger}
            onClick={() => onAnswer(true)}
          >
            {options.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Returns an awaitable confirm() and the modal element to render while one is open. */
export function useConfirmModal(): [(options: ConfirmOptions) => Promise<boolean>, ReactNode] {
  const [pending, setPending] = useState<{
    options: ConfirmOptions;
    resolve: (ok: boolean) => void;
  } | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ options, resolve })),
    []
  );

  const onAnswer = useCallback(
    (ok: boolean) => {
      pending?.resolve(ok);
      setPending(null);
    },
    [pending]
  );

  const modal = pending ? <ConfirmModal options={pending.options} onAnswer={onAnswer} /> : null;
  return [confirm, modal];
}
